import { useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { usePermissionStore } from '@/stores/permissionStore'
import {
  type PermissionId,
  hasAnyPermissionId,
  hasCodenamePermission,
  hasPermissionId,
} from '@/lib/permissions'

/**
 * Convenience hook wrapping authStore with derived values:
 * - isAdmin: whether the user is a company admin
 * - hasPermission(id): check if user has a specific permission ID
 * - hasAnyPermission(ids): check if user has any of the given IDs
 * - hasCodename(codenames): resolve codenames via catalog map
 */
export function useAuth() {
  const { user, isAuthenticated, logout, updateUser } = useAuthStore()
  const storePermissions = usePermissionStore((s) => s.allPermissionList)
  const codenameToId = usePermissionStore((s) => s.codenameToId)

  const isAdmin = useMemo(() => user?.is_company_admin ?? false, [user])

  const currentCompany = useMemo(() => {
    if (!user?.company) return null
    if (typeof user.company === 'object') return user.company
    return null
  }, [user])

  const currentProfile = user
  const permissions = storePermissions ?? user?.permissions ?? []

  const hasPermission = useMemo(
    () => (permId: PermissionId) => hasPermissionId(permissions, permId, isAdmin),
    [permissions, isAdmin],
  )

  const hasAnyPermission = useMemo(
    () => (permIds: PermissionId[]) =>
      hasAnyPermissionId(permissions, permIds, isAdmin),
    [permissions, isAdmin],
  )

  const hasCodename = useMemo(
    () => (codenames: string | string[], requireAll = false) =>
      hasCodenamePermission(codenames, permissions, codenameToId, isAdmin, requireAll),
    [permissions, codenameToId, isAdmin],
  )

  return {
    user,
    currentProfile,
    currentCompany,
    isAuthenticated,
    isAdmin,
    permissions,
    codenameToId,
    hasPermission,
    hasAnyPermission,
    hasCodename,
    logout,
    updateUser,
  }
}
